import axios from 'axios';
import { devWarn, devDebug } from './lib/log';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, timeout: 25000 });

// Feed failures are expected (rate limits, network) — the UI prints "no signal".
const logFeed = (name, err) => {
  devWarn(`[nanovram] ${name} feed failed:`, err?.message || err);
};

export const getQuote = async () => {
  try { return (await http.get('/quote')).data.eth_usd; }
  catch (err) { logFeed('quote', err); return null; }
};

export const getWire = async () => {
  try { return (await http.get('/wire')).data.coins; }
  catch (err) { logFeed('wire', err); return null; }
};

export const getStats = async () => {
  try { return (await http.get('/stats')).data; }
  catch (err) { logFeed('stats', err); return null; }
};

export const getDiesAvailability = async (fresh = false) => {
  try { return (await http.get(`/dies/availability?fresh=${fresh}`)).data.dies; }
  catch (err) { logFeed('availability', err); return null; }
};

export const getCoin = async (coinId) => {
  try { return (await http.get(`/coin/${coinId}`)).data; }
  catch (err) { logFeed(`coin:${coinId}`, err); return null; }
};

export const createRental = async (payload) => {
  try {
    return (await http.post('/rentals', payload)).data;
  } catch (err) {
    const reason = err?.response?.data?.detail;
    throw new Error(reason || err?.message || 'rental could not be created');
  }
};

export const getRentals = async (wallet, token) => {
  try {
    const res = await http.get(`/rentals/${wallet}`, { headers: { Authorization: `Bearer ${token}` } });
    return res.data.rentals;
  } catch (err) { logFeed('rentals', err); return []; }
};

export const getRentalStatus = async (orderId) => {
  try { return (await http.get(`/rentals/order/${orderId}`)).data; }
  catch (err) { logFeed('rental-status', err); return null; }
};

export const stopRental = async (orderId, token) => {
  try {
    const res = await http.post(`/rentals/order/${orderId}/stop`, {}, { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  } catch (err) { logFeed('rental-stop', err); return null; }
};

/* ---------------- renter wallet sign-in ---------------- */
export const getWalletChallenge = async (address) => (await http.post('/auth/nonce', { address })).data.message;

export const verifyWallet = async (address, message, signature) => {
  const res = await http.post('/auth/verify', { address, message, signature });
  return res.data.access_token;
};

/* ---------------- owner console (wallet-signature auth) ---------------- */
export const getOwnerChallenge = async () => (await http.post('/admin/nonce')).data.message;

export const verifyOwner = async (address, message, signature) => {
  const res = await http.post('/admin/verify', { address, message, signature });
  return res.data.access_token;
};

export const getFleet = async (token, live = false) => {
  const res = await http.get(`/admin/fleet?live=${live}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const refundRental = async (orderId, token, refundTx) => {
  try {
    const res = await http.post(`/admin/rentals/${orderId}/refund`, { refund_tx: refundTx || null },
      { headers: { Authorization: `Bearer ${token}` } });
    return res.data;
  } catch (err) { logFeed('refund', err); return null; }
};

export const getByteHistory = async (sessionId) => {
  try { return (await http.get(`/byte/history/${sessionId}`)).data.messages; }
  catch (err) { logFeed('byte-history', err); return []; }
};

export const getSessionId = () => {
  let id = localStorage.getItem('nvrm_session');
  if (!id) {
    id = 'desk-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem('nvrm_session', id);
  }
  return id;
};

// Stable unique id for chat messages (used as React keys)
export const mkMsgId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const parseSseFrame = (frame, onDelta) => {
  const line = frame.trim();
  if (!line.startsWith('data:')) return false;
  try {
    const payload = JSON.parse(line.slice(5).trim());
    if (payload.delta) onDelta(payload.delta);
    return Boolean(payload.done);
  } catch (err) {
    // Partial frame split across network chunks — harmless, next read completes it.
    devDebug('[nanovram] partial SSE frame skipped:', err?.message);
    return false;
  }
};

// Stream BYTE's reply over SSE; onDelta(token) fires per chunk.
export const streamByte = async (sessionId, message, onDelta) => {
  const res = await fetch(`${API}/byte/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  if (!res.ok || !res.body) throw new Error('byte line is down');
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split('\n\n');
    buf = parts.pop();
    for (const part of parts) {
      if (parseSseFrame(part, onDelta)) return;
    }
  }
};
